import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Calendar, Clock, Send, Sparkles, Loader2, CheckCircle2, XCircle, HourglassIcon, Pencil, X, Trash2 } from 'lucide-react';
import { API, useAdminAuth } from '../context/AdminAuthContext';
import DeskButton from '../components/DeskButton';
import AdminLayout from '../components/AdminLayout';
import OvertimeSlaChip from '../components/OvertimeSlaChip';

/**
 * Feb 2026 — AdminMyWork
 *
 * Per-user work-tracking hub. Overtime request submission +
 * personal history + desk activity log. Split into two cards so
 * users can jump straight to the one they need.
 */

const mudDate = () => {
  const d = new Date();
  const shifted = new Date(d.getTime() + 4 * 60 * 60 * 1000);
  return shifted.toISOString().slice(0, 10);
};

const StatusChip = ({ status }) => {
  const map = {
    pending:  { bg: '#FEF3C7', ink: '#92400E', label: 'Pending',  Icon: HourglassIcon },
    approved: { bg: '#D1FAE5', ink: '#065F46', label: 'Approved', Icon: CheckCircle2 },
    rejected: { bg: '#FEE2E2', ink: '#991B1B', label: 'Rejected', Icon: XCircle },
  };
  const s = map[status] || map.pending;
  const Icon = s.Icon;
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
      style={{ background: s.bg, color: s.ink }}
    >
      <Icon className="w-3 h-3" /> {s.label}
    </span>
  );
};

const AdminMyWork = () => {
  const { authHeaders, isSuperAdmin, dutyMeta, warehouses, warehouseScopeAll } = useAdminAuth();
  const [otDate, setOtDate] = useState(mudDate());
  const [otHours, setOtHours] = useState(2);
  const [otReason, setOtReason] = useState('');
  // Feb 2026 — Overtime is fully auto-computed on the backend:
  // start_time = user.duty_end, end_time = start + hours. The form
  // no longer collects start/end/minutes directly — only whole
  // hours + reason. We surface a live preview below so the user
  // can eyeball what will get submitted.
  const dutyEnd = (dutyMeta?.duty_end || '').trim();
  const parseHHMM = (s) => {
    const m = /^([01]\d|2[0-3]):([0-5]\d)$/.exec((s || '').trim());
    return m ? parseInt(m[1], 10) * 60 + parseInt(m[2], 10) : null;
  };
  const fmtHHMM = (mins) => {
    const clamped = Math.min(Math.max(mins || 0, 0), 24 * 60 - 1);
    const h = Math.floor(clamped / 60);
    const m = clamped % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  };
  const previewStart = dutyEnd || '';
  const previewEnd = (() => {
    const base = parseHHMM(previewStart);
    if (base === null) return '';
    return fmtHHMM(base + Math.max(Number(otHours) || 0, 0) * 60);
  })();
  const [autoClaim, setAutoClaim] = useState(null);
  const [autoLoading, setAutoLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [requests, setRequests] = useState([]);
  const [desk, setDesk] = useState(null);
  const [deskLoading, setDeskLoading] = useState(true);
  // Aug 2026 — requester self-edit dialog (pending rows only).
  const [editing, setEditing] = useState(null); // { id, date, hours, minutes, reason }
  const [editSaving, setEditSaving] = useState(false);

  const loadRequests = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/overtime-requests`, {
        params: { scope: 'mine' },
        headers: authHeaders,
      });
      setRequests(data.requests || []);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Failed to load overtime history';
      toast.error(msg);
    }
  }, [authHeaders]);

  const loadDesk = useCallback(async () => {
    setDeskLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/desk/mine`, { headers: authHeaders });
      setDesk(data);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Failed to load desk activity';
      toast.error(msg);
    } finally { setDeskLoading(false); }
  }, [authHeaders]);

  useEffect(() => { loadRequests(); loadDesk(); }, [loadRequests, loadDesk]);

  // Auto-fill lookup whenever the date changes.
  const runAutoLookup = useCallback(async (dt) => {
    if (!dt) return;
    setAutoLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/overtime-requests/auto-claim`, {
        params: { date: dt },
        headers: authHeaders,
      });
      setAutoClaim(data);
    } catch (e) {
      setAutoClaim(null);
    } finally { setAutoLoading(false); }
  }, [authHeaders]);

  useEffect(() => { runAutoLookup(otDate); }, [otDate, runAutoLookup]);

  const applyAutoClaim = () => {
    if (!autoClaim || !autoClaim.duration_minutes) return;
    const total = autoClaim.duration_minutes;
    // Feb 2026 — pre-fill an integer hour count from the row's stored
    // duration; the edit dialog collects only whole hours.
    setOtHours(Math.max(1, Math.round(total / 60)));
    toast.success(`Duration filled from attendance: ${autoClaim.duration_display}`);
  };

  const submitRequest = async () => {
    const hours = Math.max(0, Math.floor(Number(otHours) || 0));
    const total = hours * 60;
    if (total < 1) {
      toast.error('Enter at least 1 hour.');
      return;
    }
    if (total > 16 * 60) {
      toast.error('Duration cannot exceed 16 hours per request.');
      return;
    }
    setSubmitting(true);
    try {
      // Feb 2026 — no start/end on the payload; backend auto-derives
      // from the requester's duty_end + this duration.
      await axios.post(`${API}/admin/overtime-requests`, {
        date: otDate,
        duration_minutes: total,
        reason: otReason.trim(),
      }, { headers: authHeaders });
      toast.success('Overtime request submitted for review.');
      setOtHours(2); setOtReason('');
      loadRequests();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Failed to submit request';
      toast.error(msg);
    } finally { setSubmitting(false); }
  };

  const openEdit = (r) => {
    const mins = Number(r.requested_minutes ?? r.duration_minutes ?? 0);
    setEditing({
      id: r.id,
      date: r.date,
      hours: Math.max(1, Math.round(mins / 60)),
      reason: r.reason || '',
    });
  };

  const saveEdit = async () => {
    if (!editing) return;
    const hours = Math.max(0, Math.floor(Number(editing.hours) || 0));
    const total = hours * 60;
    if (total < 1) { toast.error('Enter at least 1 hour.'); return; }
    if (total > 16 * 60) { toast.error('Duration cannot exceed 16 hours per request.'); return; }
    setEditSaving(true);
    try {
      await axios.patch(`${API}/admin/overtime-requests/${editing.id}`, {
        date: editing.date,
        duration_minutes: total,
        reason: (editing.reason || '').trim(),
      }, { headers: authHeaders });
      toast.success('Request updated.');
      setEditing(null);
      loadRequests();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Failed to update request';
      toast.error(msg);
    } finally { setEditSaving(false); }
  };

  // Aug 2026 — Super-admin overrides on the user's own history.
  const deleteOvertime = async (row) => {
    if (!window.confirm(`Delete overtime request for ${row.date}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/overtime-requests/${row.id}`, { headers: authHeaders });
      toast.success('Overtime request deleted.');
      loadRequests();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete');
    }
  };

  const [deskEditing, setDeskEditing] = useState(null);
  const [deskEditSaving, setDeskEditSaving] = useState(false);

  const openDeskEdit = (row) => {
    const toLocalInput = (iso) => {
      if (!iso) return '';
      const d = new Date(iso);
      const pad = (n) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };
    setDeskEditing({
      row,
      category: row.category || 'other',
      reason: row.reason || '',
      outAt: toLocalInput(row.out_at),
      inAt: toLocalInput(row.in_at),
      outcome: row.outcome || '',
      failureReason: row.failure_reason || '',
    });
  };

  const saveDeskEdit = async () => {
    if (!deskEditing) return;
    if (!deskEditing.outAt) { toast.error('Out timestamp is required.'); return; }
    if (deskEditing.outcome === 'unsuccess' && !deskEditing.failureReason.trim()) {
      toast.error('Failure reason is required when outcome is Unsuccess.');
      return;
    }
    setDeskEditSaving(true);
    try {
      const body = {
        category: deskEditing.category,
        reason: (deskEditing.reason || '').trim(),
        out_at: new Date(deskEditing.outAt).toISOString(),
        in_at: deskEditing.inAt ? new Date(deskEditing.inAt).toISOString() : null,
        outcome: deskEditing.outcome || '',
        failure_reason: (deskEditing.failureReason || '').trim(),
      };
      await axios.put(`${API}/admin/desk-activity/${deskEditing.row.id}/admin-edit`, body, { headers: authHeaders });
      toast.success('Desk row updated.');
      setDeskEditing(null);
      loadDesk();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update desk row');
    } finally { setDeskEditSaving(false); }
  };

  const deleteDeskRow = async (row) => {
    if (!window.confirm('Delete this desk-activity row? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/admin/desk-activity/${row.id}`, { headers: authHeaders });
      toast.success('Desk row deleted.');
      loadDesk();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete');
    }
  };

  const cardCls = 'rounded-2xl border border-slate-200 bg-white overflow-hidden';

  return (
    <AdminLayout>
      <div className="px-4 sm:px-6 py-6 space-y-6 max-w-6xl" data-testid="my-work-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900">My work</h1>
          <p className="text-sm text-slate-500 mt-1">
            Submit overtime claims and track when you step away from your desk.
          </p>
        </div>
        <DeskButton onChanged={loadDesk} />
      </div>

      {/* Feb 2026 — My Warehouses card. Renders the resolved warehouse
          names surfaced by `/admin/me` so users can confirm their
          assignment at a glance. Super admin + `["*"]` scope see an
          "All warehouses" banner listing every active WH. Users with
          no warehouse binding see a neutral "None assigned" pill so
          they can raise it with their manager. */}
      <div className={cardCls} data-testid="my-work-warehouses-card">
        <div className="px-5 pt-5 pb-3 border-b border-slate-100 flex flex-wrap items-baseline justify-between gap-2">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Warehouse access</div>
            <div className="text-lg font-extrabold text-slate-900">
              {warehouseScopeAll
                ? 'All warehouses'
                : `My warehouses (${(warehouses || []).length})`}
            </div>
          </div>
          {warehouseScopeAll && (
            <span className="text-[10px] uppercase tracking-wider font-bold text-emerald-700 bg-emerald-100 border border-emerald-200 px-2 py-1 rounded-full">
              Unrestricted
            </span>
          )}
        </div>
        <div className="px-5 py-4">
          {Array.isArray(warehouses) && warehouses.length > 0 ? (
            <ul className="flex flex-wrap gap-2" data-testid="my-work-warehouses-list">
              {warehouses.map((w) => (
                <li
                  key={w.id}
                  data-testid={`my-work-warehouse-${w.id}`}
                  className="inline-flex items-center gap-1 rounded-full bg-sky-50 border border-sky-200 text-sky-900 px-3 py-1 text-sm font-semibold"
                  title={w.is_default ? 'Default Warehouse — holds unassigned stock' : w.name}
                >
                  {w.name}
                  {w.is_default && (
                    <span className="text-[9px] uppercase tracking-wider font-bold text-amber-700 bg-amber-100 border border-amber-200 px-1.5 py-0.5 rounded-full">
                      Default
                    </span>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-slate-500" data-testid="my-work-warehouses-empty">
              No warehouse is currently assigned to your account. Ask an admin to update your access.
            </p>
          )}
        </div>
      </div>

      {/* Overtime request card */}
      <div className={cardCls}>
        <div className="px-5 pt-5 pb-3 border-b border-slate-100">
          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Overtime</div>
          <div className="text-lg font-extrabold text-slate-900">Submit a new request</div>
        </div>
        <div className="px-5 py-4 grid grid-cols-1 md:grid-cols-3 gap-4">
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500 flex items-center gap-1">
              <Calendar className="w-3 h-3" /> Date
            </span>
            <input
              type="date"
              value={otDate}
              min={mudDate()}
              onChange={(e) => setOtDate(e.target.value)}
              className="mt-1 w-full h-10 rounded-lg border border-slate-300 px-3 text-sm focus:ring-2 focus:ring-sky-500 focus:outline-none"
              data-testid="ot-date-input"
            />
          </label>
          <div className="block">
            <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500 flex items-center gap-1">
              <Clock className="w-3 h-3" /> Overtime hours
            </span>
            <input
              type="number" min="1" max="16" step="1" value={otHours}
              onChange={(e) => setOtHours(Math.max(1, Math.min(16, parseInt(e.target.value || '0', 10))))}
              placeholder="e.g. 2"
              className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm text-center focus:ring-2 focus:ring-sky-500 focus:outline-none"
              data-testid="ot-hours-input"
              aria-label="Overtime hours"
            />
            <div className="mt-1 text-[10px] text-slate-500">
              {dutyEnd ? (
                <span data-testid="ot-window-preview">
                  Auto: <b className="font-mono">{previewStart}</b> → <b className="font-mono">{previewEnd}</b> ({otHours}h)
                </span>
              ) : (
                'Your duty_end is not on file — overtime cannot be auto-computed. Ask HR to set your schedule.'
              )}
            </div>
          </div>
          <div>
            <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500 flex items-center gap-1">
              <Sparkles className="w-3 h-3" /> Auto-fill from attendance
            </span>
            <button
              type="button"
              onClick={applyAutoClaim}
              disabled={!autoClaim || !autoClaim.duration_minutes || autoLoading}
              className="mt-1 w-full h-10 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-xs font-bold disabled:opacity-40 flex items-center justify-center gap-2"
              data-testid="ot-autoclaim-button"
            >
              {autoLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                autoClaim?.duration_minutes
                  ? <>Claim {autoClaim.duration_display} from attendance</>
                  : <>No overtime detected on that date</>
              )}
            </button>
          </div>
          <label className="block md:col-span-3">
            <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Reason</span>
            <textarea
              value={otReason}
              onChange={(e) => setOtReason(e.target.value)}
              placeholder="Briefly describe what you worked on…"
              className="mt-1 w-full min-h-[72px] rounded-lg border border-slate-300 p-3 text-sm focus:ring-2 focus:ring-sky-500 focus:outline-none"
              data-testid="ot-reason-input"
            />
          </label>
          <div className="md:col-span-3 flex justify-end">
            <button
              type="button"
              onClick={submitRequest}
              disabled={submitting}
              className="h-10 px-5 rounded-lg bg-slate-900 hover:bg-slate-950 text-white text-sm font-bold flex items-center gap-2 disabled:opacity-60"
              data-testid="ot-submit-button"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Submit request
            </button>
          </div>
        </div>
      </div>

      {/* Overtime history */}
      <div className={cardCls}>
        <div className="px-5 py-3 border-b border-slate-100 text-sm font-bold text-slate-900">
          My overtime requests
        </div>
        {requests.length === 0 ? (
          <div className="px-5 py-6 text-sm text-slate-500 italic">No requests submitted yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-2">Date</th>
                  <th className="text-left px-4 py-2">Start</th>
                  <th className="text-left px-4 py-2">End</th>
                  <th className="text-left px-4 py-2">Total</th>
                  <th className="text-left px-4 py-2">Approved</th>
                  <th className="text-left px-4 py-2">Reason</th>
                  <th className="text-center px-4 py-2">Status</th>
                  <th className="text-left px-4 py-2">Reviewer note</th>
                  <th className="text-left px-4 py-2">Reviewed</th>
                  <th className="text-right px-4 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {requests.map((r) => (
                  <tr key={r.id} data-testid={`ot-row-${r.id}`}>
                    <td className="px-4 py-2 font-mono">{r.date}</td>
                    <td className="px-4 py-2 font-mono text-slate-700" data-testid={`ot-start-${r.id}`}>{r.start_time || '—'}</td>
                    <td className="px-4 py-2 font-mono text-slate-700" data-testid={`ot-end-${r.id}`}>
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span>{r.end_time || '—'}</span>
                        <OvertimeSlaChip row={r} dataTestPrefix="ot-mine-sla" />
                      </div>
                    </td>
                    <td className="px-4 py-2 font-semibold" data-testid={`ot-total-${r.id}`}>{r.requested_display || r.duration_display}</td>
                    <td className="px-4 py-2 font-semibold text-emerald-700">{r.approved_display || '—'}</td>
                    <td className="px-4 py-2 text-slate-600 max-w-[320px] truncate" title={r.reason}>{r.reason || '—'}</td>
                    <td className="px-4 py-2 text-center"><StatusChip status={r.status} /></td>
                    <td className="px-4 py-2 text-slate-600 max-w-[220px] truncate" title={r.review_note}>{r.review_note || '—'}</td>
                    <td className="px-4 py-2 text-slate-500">{r.reviewed_at_display || '—'}</td>
                    <td className="px-4 py-2 text-right">
                      <div className="inline-flex items-center gap-1 justify-end">
                        {r.status === 'pending' && (
                          <button
                            type="button"
                            onClick={() => openEdit(r)}
                            className="inline-flex items-center gap-1 h-7 px-2 rounded-md border border-slate-300 hover:bg-slate-50 text-[11px] font-semibold text-slate-700"
                            data-testid={`ot-edit-${r.id}`}
                            title="Edit this pending request"
                          >
                            <Pencil className="w-3 h-3" /> Edit
                          </button>
                        )}
                        {isSuperAdmin && (
                          <button
                            type="button"
                            onClick={() => deleteOvertime(r)}
                            className="inline-flex items-center gap-1 h-7 px-2 rounded-md border border-red-200 hover:bg-red-50 text-[11px] font-semibold text-red-700"
                            data-testid={`ot-delete-${r.id}`}
                            title="Super-admin delete"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Requester edit dialog */}
      {editing && (
        <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center p-4" data-testid="ot-edit-dialog">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
              <div className="text-sm font-bold text-slate-900">Edit overtime request</div>
              <button type="button" onClick={() => setEditing(null)} className="text-slate-400 hover:text-slate-600" data-testid="ot-edit-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="px-5 py-4 space-y-3">
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Date</span>
                <input
                  type="date"
                  value={editing.date}
                  min={mudDate()}
                  onChange={(e) => setEditing((v) => ({ ...v, date: e.target.value }))}
                  className="mt-1 w-full h-10 rounded-lg border border-slate-300 px-3 text-sm"
                  data-testid="ot-edit-date"
                />
              </label>
              <div>
                <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Overtime hours</span>
                <input
                  type="number" min="1" max="16" step="1" value={editing.hours}
                  onChange={(e) => setEditing((v) => ({ ...v, hours: Math.max(1, Math.min(16, parseInt(e.target.value || '0', 10))) }))}
                  className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm text-center"
                  data-testid="ot-edit-hours"
                />
                <div className="mt-1 text-[10px] text-slate-500">
                  Backend will re-derive the window from your <span className="font-mono">duty_end</span> and this duration.
                </div>
              </div>
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Reason</span>
                <textarea
                  value={editing.reason}
                  onChange={(e) => setEditing((v) => ({ ...v, reason: e.target.value }))}
                  className="mt-1 w-full min-h-[72px] rounded-lg border border-slate-300 p-3 text-sm"
                  data-testid="ot-edit-reason"
                />
              </label>
            </div>
            <div className="px-5 py-3 border-t border-slate-100 flex justify-end gap-2">
              <button type="button" onClick={() => setEditing(null)} className="h-9 px-4 rounded-lg border border-slate-300 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                Cancel
              </button>
              <button
                type="button"
                onClick={saveEdit}
                disabled={editSaving}
                className="h-9 px-4 rounded-lg bg-slate-900 hover:bg-slate-950 text-white text-sm font-bold flex items-center gap-1 disabled:opacity-60"
                data-testid="ot-edit-save"
              >
                {editSaving && <Loader2 className="w-4 h-4 animate-spin" />} Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Desk activity log */}
      <div className={cardCls}>
        <div className="px-5 py-3 border-b border-slate-100 text-sm font-bold text-slate-900">
          Desk activity — <span className="font-normal text-slate-500">{desk?.month || ''}</span>
        </div>
        {deskLoading ? (
          <div className="px-5 py-6 text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>
        ) : (desk?.rows || []).length === 0 ? (
          <div className="px-5 py-6 text-sm text-slate-500 italic">No desk activity recorded this month.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-2">Out</th>
                  <th className="text-left px-4 py-2">In</th>
                  <th className="text-left px-4 py-2">Duration</th>
                  <th className="text-left px-4 py-2">Category</th>
                  <th className="text-left px-4 py-2">Leave reason</th>
                  <th className="text-left px-4 py-2">Outcome</th>
                  <th className="text-left px-4 py-2">Failure reason</th>
                  {isSuperAdmin && <th className="text-right px-4 py-2" />}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(desk?.rows || []).map((r) => (
                  <tr key={r.id} data-testid={`desk-row-${r.id}`}>
                    <td className="px-4 py-2 tabular-nums">{r.out_at_display || '—'}</td>
                    <td className="px-4 py-2 tabular-nums">{r.in_at_display || <span className="text-amber-700 font-semibold">Open</span>}</td>
                    <td className="px-4 py-2 font-semibold">{r.duration_display || '—'}</td>
                    <td className="px-4 py-2">
                      <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-slate-100 text-slate-700">
                        {r.category_label}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-slate-600 max-w-[240px] truncate" title={r.reason}>{r.reason || '—'}</td>
                    <td className="px-4 py-2">
                      {r.outcome === 'success' && (
                        <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800" data-testid={`desk-outcome-${r.id}`}>Success</span>
                      )}
                      {r.outcome === 'unsuccess' && (
                        <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-red-100 text-red-800" data-testid={`desk-outcome-${r.id}`}>Unsuccess</span>
                      )}
                      {!r.outcome && <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-4 py-2 text-slate-600 max-w-[280px] truncate" title={r.failure_reason}>{r.failure_reason || '—'}</td>
                    {isSuperAdmin && (
                      <td className="px-4 py-2 text-right">
                        <div className="inline-flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => openDeskEdit(r)}
                            className="inline-flex items-center gap-1 h-7 px-2 rounded-md border border-slate-300 hover:bg-slate-50 text-[11px] font-semibold text-slate-700"
                            data-testid={`desk-edit-${r.id}`}
                            title="Super-admin edit"
                          >
                            <Pencil className="w-3 h-3" />
                          </button>
                          <button
                            type="button"
                            onClick={() => deleteDeskRow(r)}
                            className="inline-flex items-center gap-1 h-7 px-2 rounded-md border border-red-200 hover:bg-red-50 text-[11px] font-semibold text-red-700"
                            data-testid={`desk-delete-${r.id}`}
                            title="Super-admin delete"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Super-admin desk-activity edit dialog */}
      {deskEditing && (
        <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center p-4" data-testid="mw-desk-edit-dialog">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden max-h-[92vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
              <div className="text-sm font-bold text-slate-900">
                Edit desk-activity row
                <span className="ml-2 text-[10px] font-semibold text-fuchsia-600 uppercase tracking-wider">SUPER-ADMIN</span>
              </div>
              <button type="button" onClick={() => setDeskEditing(null)} className="text-slate-400 hover:text-slate-600" data-testid="mw-desk-edit-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="px-5 py-4 space-y-3 overflow-y-auto">
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Category</span>
                <select
                  value={deskEditing.category}
                  onChange={(e) => setDeskEditing((v) => ({ ...v, category: e.target.value }))}
                  className="mt-1 w-full h-10 rounded-lg border border-slate-300 px-3 text-sm"
                  data-testid="mw-desk-edit-category"
                >
                  <option value="prayer">Prayer</option>
                  <option value="lunch">Lunch</option>
                  <option value="meeting">Meeting</option>
                  <option value="personal">Personal</option>
                  <option value="cash_deposit">Cash Deposit</option>
                  <option value="other">Other</option>
                </select>
              </label>
              <div className="grid grid-cols-2 gap-2">
                <label className="block">
                  <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Out</span>
                  <input
                    type="datetime-local"
                    value={deskEditing.outAt}
                    onChange={(e) => setDeskEditing((v) => ({ ...v, outAt: e.target.value }))}
                    className="mt-1 w-full h-10 rounded-lg border border-slate-300 px-3 text-sm"
                    data-testid="mw-desk-edit-out"
                  />
                </label>
                <label className="block">
                  <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">In (blank = open)</span>
                  <input
                    type="datetime-local"
                    value={deskEditing.inAt}
                    onChange={(e) => setDeskEditing((v) => ({ ...v, inAt: e.target.value }))}
                    className="mt-1 w-full h-10 rounded-lg border border-slate-300 px-3 text-sm"
                    data-testid="mw-desk-edit-in"
                  />
                </label>
              </div>
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Leave reason</span>
                <textarea
                  value={deskEditing.reason}
                  onChange={(e) => setDeskEditing((v) => ({ ...v, reason: e.target.value }))}
                  className="mt-1 w-full min-h-[64px] rounded-lg border border-slate-300 p-3 text-sm"
                  data-testid="mw-desk-edit-reason"
                />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Outcome</span>
                <select
                  value={deskEditing.outcome}
                  onChange={(e) => setDeskEditing((v) => ({ ...v, outcome: e.target.value }))}
                  className="mt-1 w-full h-10 rounded-lg border border-slate-300 px-3 text-sm"
                  data-testid="mw-desk-edit-outcome"
                >
                  <option value="">— (still open)</option>
                  <option value="success">Success</option>
                  <option value="unsuccess">Unsuccess</option>
                </select>
              </label>
              {deskEditing.outcome === 'unsuccess' && (
                <label className="block">
                  <span className="text-[10px] uppercase tracking-wider font-bold text-red-700">Failure reason (required)</span>
                  <textarea
                    value={deskEditing.failureReason}
                    onChange={(e) => setDeskEditing((v) => ({ ...v, failureReason: e.target.value }))}
                    className="mt-1 w-full min-h-[64px] rounded-lg border border-red-300 p-3 text-sm"
                    data-testid="mw-desk-edit-failure-reason"
                  />
                </label>
              )}
            </div>
            <div className="px-5 py-3 border-t border-slate-100 flex justify-end gap-2">
              <button type="button" onClick={() => setDeskEditing(null)} className="h-9 px-4 rounded-lg border border-slate-300 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                Cancel
              </button>
              <button
                type="button"
                onClick={saveDeskEdit}
                disabled={deskEditSaving}
                className="h-9 px-4 rounded-lg bg-slate-900 hover:bg-slate-950 text-white text-sm font-bold flex items-center gap-1 disabled:opacity-60"
                data-testid="mw-desk-edit-save"
              >
                {deskEditSaving && <Loader2 className="w-4 h-4 animate-spin" />} Save
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </AdminLayout>
  );
};

export default AdminMyWork;
